/**
 * @tankef/telemetry - Recolección de datos para observabilidad
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 *                         ┌─────────────────────┐
 *                         │   Tu Aplicación     │
 *                         └──────────┬──────────┘
 *                                    │
 *                         ┌──────────▼──────────┐
 *                         │  @tankef/telemetry  │
 *                         │                     │
 *                         │  • log()            │  ← Logs estructurados
 *                         │  • captureError()   │  ← Errores a Sentry
 *                         │  • track()          │  ← Eventos de usuario
 *                         │  • startSpan()      │  ← Métricas de tiempo
 *                         │                     │
 *                         └──────────┬──────────┘
 *                                    │
 *              ┌─────────────────────┼─────────────────────┐
 *              ▼                     ▼                     ▼
 *        ┌──────────┐          ┌──────────┐          ┌──────────┐
 *        │  Sentry  │          │ Mixpanel │          │  Console │
 *        │          │          │          │          │          │
 *        │ Errors   │          │ Analytics│          │  Debug   │
 *        │ Traces   │          │ Events   │          │  Logs    │
 *        └──────────┘          └──────────┘          └──────────┘
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Este package es SOLO para recolección de datos (telemetría).
 * La visualización y análisis (observabilidad) se hace en herramientas externas.
 *
 * Características:
 * - 100% compartible con React Native (futuro)
 * - Type-safe (eventos tipados)
 * - PII sanitization (no envía datos sensibles)
 */

// ═══════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════

/** Niveles de log estándar */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Contexto adicional para logs */
export interface LogContext {
  /** ID del usuario actual */
  userId?: string;
  /** ID de la sesión */
  sessionId?: string;
  /** Feature donde ocurrió */
  feature?: string;
  /** Cualquier dato adicional */
  [key: string]: unknown;
}

/** Evento de analytics */
export interface AnalyticsEvent {
  /** Nombre del evento (usar AnalyticsEvents constants) */
  name: string;
  /** Propiedades del evento */
  properties?: Record<string, unknown>;
}

/** Span para medir tiempo */
export interface Span {
  name: string;
  startTime: number;
  endTime?: number;
  attributes?: Record<string, unknown>;
}

/** Configuración de telemetría */
export interface TelemetryConfig {
  sentry: {
    dsn: string;
    environment: 'development' | 'staging' | 'production';
    release?: string;
    /** % de traces a capturar (0.0 - 1.0) */
    tracesSampleRate?: number;
  };
  analytics?: {
    enabled?: boolean;
    /** Imprimir eventos en consola (desarrollo) */
    debug?: boolean;
  };
}

// ═══════════════════════════════════════════════════════════════
// INTERFACE DEL PROVIDER
// Esta interface la implementan WebTelemetryProvider y NativeTelemetryProvider
// ═══════════════════════════════════════════════════════════════

export interface TelemetryProvider {
  // ─────────────────────────────────────────────
  // LOGS - "Qué pasó"
  // ─────────────────────────────────────────────
  log(level: LogLevel, message: string, context?: LogContext): void;

  // ─────────────────────────────────────────────
  // ERRORS - "Qué falló"
  // ─────────────────────────────────────────────
  captureError(error: Error, context?: LogContext): void;

  // ─────────────────────────────────────────────
  // ANALYTICS - "Qué hizo el usuario"
  // ─────────────────────────────────────────────
  track(event: AnalyticsEvent): void;
  identify(userId: string, traits?: Record<string, unknown>): void;

  // ─────────────────────────────────────────────
  // PERFORMANCE - "Cuánto tardó"
  // ─────────────────────────────────────────────
  startSpan(name: string, attributes?: Record<string, unknown>): Span;
  endSpan(span: Span): void;

  // ─────────────────────────────────────────────
  // USER CONTEXT
  // ─────────────────────────────────────────────
  setUser(user: { id: string; email?: string } | null): void;
}

// ═══════════════════════════════════════════════════════════════
// EVENTOS DE ANALYTICS - Type-safe
// ═══════════════════════════════════════════════════════════════

/**
 * Catálogo de eventos de analytics
 *
 * Convención: RECURSO_ACCION
 *
 * Usar estos en lugar de strings para:
 * - Autocompletado
 * - Evitar typos
 * - Documentación centralizada
 */
export const AnalyticsEvents = {
  // ─────────────────────────────────────────────
  // AUTH
  // ─────────────────────────────────────────────
  /** Usuario inició el flujo de login */
  LOGIN_STARTED: 'login_started',
  /** Login exitoso */
  LOGIN_SUCCESS: 'login_success',
  /** Login fallido */
  LOGIN_FAILED: 'login_failed',
  /** Logout */
  LOGOUT: 'logout',
  /** MFA solicitado */
  MFA_REQUESTED: 'mfa_requested',
  /** MFA completado */
  MFA_SUCCESS: 'mfa_success',
  /** MFA fallido */
  MFA_FAILED: 'mfa_failed',

  // ─────────────────────────────────────────────
  // TRANSFERS
  // ─────────────────────────────────────────────
  /** Usuario abrió formulario de transferencia */
  TRANSFER_STARTED: 'transfer_started',
  /** Usuario ingresó monto */
  TRANSFER_AMOUNT_ENTERED: 'transfer_amount_entered',
  /** Usuario seleccionó beneficiario */
  TRANSFER_BENEFICIARY_SELECTED: 'transfer_beneficiary_selected',
  /** Usuario llegó a pantalla de confirmación */
  TRANSFER_CONFIRMED: 'transfer_confirmed',
  /** Transferencia exitosa */
  TRANSFER_SUCCESS: 'transfer_success',
  /** Transferencia fallida */
  TRANSFER_FAILED: 'transfer_failed',
  /** Comprobante compartido */
  TRANSFER_RECEIPT_SHARED: 'transfer_receipt_shared',

  // ─────────────────────────────────────────────
  // CONTACTS / BENEFICIARIES
  // ─────────────────────────────────────────────
  CONTACT_ADDED: 'contact_added',
  CONTACT_DELETED: 'contact_deleted',
  CONTACT_EDITED: 'contact_edited',

  // ─────────────────────────────────────────────
  // REGISTRO
  // ─────────────────────────────────────────────
  REGISTRATION_STARTED: 'registration_started',
  REGISTRATION_STEP_COMPLETED: 'registration_step_completed',
  REGISTRATION_COMPLETED: 'registration_completed',

  // ─────────────────────────────────────────────
  // ONBOARDING / KYC
  // ─────────────────────────────────────────────
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  KYC_STARTED: 'kyc_started',
  KYC_DOCUMENT_CAPTURED: 'kyc_document_captured',
  KYC_FACE_CAPTURED: 'kyc_face_captured',
  KYC_COMPLETED: 'kyc_completed',
  KYC_FAILED: 'kyc_failed',

  // ─────────────────────────────────────────────
  // PASSWORD RESET
  // ─────────────────────────────────────────────
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  PASSWORD_RESET_COMPLETED: 'password_reset_completed',

  // ─────────────────────────────────────────────
  // WALLET
  // ─────────────────────────────────────────────
  WALLET_VIEWED: 'wallet_viewed',
  DEPOSIT_STARTED: 'deposit_started',
  DEPOSIT_COMPLETED: 'deposit_completed',
  WITHDRAWAL_STARTED: 'withdrawal_started',
  WITHDRAWAL_COMPLETED: 'withdrawal_completed',

  // ─────────────────────────────────────────────
  // PRODUCTS (inversiones, tandas, caja de ahorro)
  // ─────────────────────────────────────────────
  PRODUCT_VIEWED: 'product_viewed',
  PRODUCT_STARTED: 'product_started',
  PRODUCT_COMPLETED: 'product_completed',
  TANDA_JOINED: 'tanda_joined',
  SAVINGS_BOX_CREATED: 'savings_box_created',
  INVESTMENT_CREATED: 'investment_created',

  // ─────────────────────────────────────────────
  // NAVIGATION
  // ─────────────────────────────────────────────
  PAGE_VIEW: 'page_view',
  FEATURE_USED: 'feature_used',

  // ─────────────────────────────────────────────
  // ERRORS
  // ─────────────────────────────────────────────
  ERROR_DISPLAYED: 'error_displayed',
  ERROR_BOUNDARY_TRIGGERED: 'error_boundary_triggered',

  // ─────────────────────────────────────────────
  // PERFORMANCE
  // ─────────────────────────────────────────────
  PERFORMANCE_SPAN: 'performance_span',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// Re-export implementations
export * from './provider';
export * from './hooks';
export * from './adapters/index';
